import React, { useState, useContext, useMemo } from 'react';

import { Button, Typography } from '@mui/material';

import SimpleTextInput from '../Components/SimpleTextInput';
import KebabInput from '../Components/KebabInput';
import RightLink from '../Components/RightLink';
import ReadOnlyInput from '../Components/ReadOnlyInput';
import NeenorAutocomplete from '../Components/NeenorAutocomplete';
import { ConsignmentDetailsModal } from '../Modals/ConsignmentModals';
import { PlaceOfLoadingModal, PlaceOfUnloadingModal } from '../Modals/TransportAndJourneyModals';

import { GBENSContext } from '../GBENSContainer';
import { useStepper } from '../../../components/Stepper';
import { dateFormatChange } from '../../../utils/date';

const UpdatingConsignments = () => {
  const [consignmentDetailsModalOpen, setConsignmentDetailsModalOpen] = useState(false);
  const [PlaceOfLoadingModalOpen, setPlaceOfLoadingModalOpen] = useState(false);
  const [PlaceOfUnloadingModalOpen, setPlaceOfUnloadingModalOpen] = useState(false);

  const { gotoStep } = useStepper();

  const screen = 'Consignments';

  const {
    currentConsignment,
    data,
    handleShowWarningModal,
    editConsignmentLine,
    addNewConsignmentLine,
    deleteConsignment,
    isReadOnly,
  } = useContext(GBENSContext);

  const clickDeleteConsignment = async () => {
    await deleteConsignment(currentConsignment.id);
    gotoStep('Consignments');
  };

  const clickEditLine = (id) => {
    if (!currentConsignment.lines.length) clickNewLine();
    else {
      editConsignmentLine(id);
      setConsignmentDetailsModalOpen(true);
    }
  };

  const clickNewLine = () => {
    addNewConsignmentLine();
    setConsignmentDetailsModalOpen(true);
  };

  const onNext = async () => {
    if (isReadOnly) {
      gotoStep('Consignments');
    } else {
      handleShowWarningModal('UpdatingConsignment', () => gotoStep('Consignments'));
    }
  };

  const TJPlaceOfLoadingPresent = useMemo(() => {
    const country = data?.TransportAndJourney?.PlaceOfLoading?.country;
    const countrySelected = country && country !== -1 && country?.trim?.() !== '';
    const placeName =
      data?.TransportAndJourney?.PlaceOfLoading?.placeName &&
      data?.TransportAndJourney?.PlaceOfLoading?.placeName?.trim?.()?.length > 0;
    const postcode =
      data?.TransportAndJourney?.PlaceOfLoading?.postcode &&
      data?.TransportAndJourney?.PlaceOfLoading?.postcode?.trim?.()?.length > 0;
    return !!(countrySelected || placeName || postcode);
  }, [data]);

  const TJPlaceOfUnloadingPresent = useMemo(() => {
    const country = data?.TransportAndJourney?.PlaceOfUnloading?.country;
    const countrySelected = country && country !== -1 && country?.trim?.() !== '';
    const placeName =
      data?.TransportAndJourney?.PlaceOfUnloading?.placeName &&
      data?.TransportAndJourney?.PlaceOfUnloading?.placeName?.trim?.()?.length > 0;
    const postcode =
      data?.TransportAndJourney?.PlaceOfUnloading?.postcode &&
      data?.TransportAndJourney?.PlaceOfUnloading?.postcode?.trim?.()?.length > 0;
    return !!(countrySelected || placeName || postcode);
  }, [data]);

  return (
    <>
      <ConsignmentDetailsModal open={consignmentDetailsModalOpen} setOpen={setConsignmentDetailsModalOpen} />
      <PlaceOfLoadingModal isConsignment open={PlaceOfLoadingModalOpen} setOpen={setPlaceOfLoadingModalOpen} />
      <PlaceOfUnloadingModal isConsignment open={PlaceOfUnloadingModalOpen} setOpen={setPlaceOfUnloadingModalOpen} />

      <div style={{ backgroundColor: '#F6F6F6', padding: 30 }}>
        <div style={{ display: 'flex', flexDirection: 'row', marginBottom: 5 }}>
          <Typography id="modal-modal-title" variant="h5" component="h2" style={{ marginLeft: 30, fontWeight: 'bold' }}>
            Consignment Header
          </Typography>
        </div>

        <SimpleTextInput
          name="Consignment Reference:"
          consignment
          screen={screen}
          id="consignmentReference"
          disabled={isReadOnly}
        />

        <NeenorAutocomplete
          name="Consignor Details:"
          modal="Consignor"
          placeholder="Type to find, or add new consignor details"
          id="Consignor"
          disabled={isReadOnly}
        />

        <NeenorAutocomplete
          name="Consignee Details:"
          modal="Consignee"
          placeholder="Type to find, or add new consignee details"
          id="Consignee"
          disabled={isReadOnly}
        />

        {TJPlaceOfLoadingPresent ? (
          <ReadOnlyInput value="Haulier's Location" name="Place of Loading:" />
        ) : (
          <KebabInput
            name="Place of Loading:"
            value={
              currentConsignment?.PlaceOfLoading?.placeName &&
              currentConsignment?.PlaceOfLoading?.placeName?.trim?.()?.length > 0
                ? currentConsignment?.PlaceOfLoading?.placeName
                : ''
            }
            placeholder="This will default to the consignors address if left blank"
            onClick={() => setPlaceOfLoadingModalOpen(true)}
            consignmentKebab
            id="PlaceOfLoading"
            screen={screen}
            disabled={isReadOnly}
          />
        )}

        {TJPlaceOfUnloadingPresent ? (
          <ReadOnlyInput value="Haulier's Location" name="Place of Unloading:" />
        ) : (
          <KebabInput
            name="Place of Unloading:"
            value={
              currentConsignment?.PlaceOfUnloading?.placeName &&
              currentConsignment?.PlaceOfUnloading?.placeName?.trim?.()?.length > 0
                ? currentConsignment?.PlaceOfUnloading?.placeName
                : ''
            }
            placeholder="This will default to the consignees address if left blank"
            onClick={() => setPlaceOfUnloadingModalOpen(true)}
            consignmentKebab
            id="PlaceOfUnloading"
            screen={screen}
            disabled={isReadOnly}
          />
        )}

        <div style={{ display: 'flex', flexDirection: 'row', marginTop: 15, marginBottom: 15 }}>
          <Typography id="modal-modal-title" variant="h5" component="h2" style={{ marginLeft: 30, fontWeight: 'bold' }}>
            Consignment Details
          </Typography>
        </div>

        {(currentConsignment?.lines?.length ? currentConsignment.lines : [{ id: 0 }])?.map?.((c, i) => {
          return (
            <KebabInput
              key={i}
              name={`Line ${i + 1}:`}
              value={c.goodsDescription}
              placeholder="Type to find, or add new Consignment Line details"
              onClick={() => clickEditLine(c.id)}
              consignmentLine
              screen={screen}
              id={c.id}
              disabled={isReadOnly}
            />
          );
        })}

        {!isReadOnly && <RightLink name="Add additional Line" onClick={clickNewLine} />}

        <div style={{ display: 'flex', flexDirection: 'row', marginTop: 15, marginBottom: 15 }}>
          <Typography id="modal-modal-title" variant="h5" component="h2" style={{ marginLeft: 30, fontWeight: 'bold' }}>
            Additional Information
          </Typography>
        </div>
        <ReadOnlyInput
          toolTip="If a CABIE Import MRN has been used to create the consignment, the reference will appear here."
          value={currentConsignment?.cabie_mrn ?? ''}
          name="CABIE Import MRN:"
        />
        <ReadOnlyInput
          toolTip="The LRN is a unique number for the consignment."
          value={currentConsignment?.localReferenceNumber ?? ''}
          name="ENS LRN:"
        />
        <ReadOnlyInput
          toolTip="The date and time the consignment was sent to HMRC."
          value={currentConsignment?.lastModified ? dateFormatChange(currentConsignment.lastModified, true) : ''}
          name="Created:"
        />
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          paddingBottom: 30,
          paddingLeft: 30,
          paddingRight: 30,
          marginTop: 30,
        }}
      >
        <Button variant="outlined" onClick={clickDeleteConsignment} disabled={isReadOnly}>
          Delete this consignment
        </Button>
        <Button variant="outlined" onClick={onNext} disabled={isReadOnly}>
          Save Consignment
        </Button>
      </div>
    </>
  );
};

export default UpdatingConsignments;
